import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';
import { TrackingApiService } from './tracking-api.service';
import { TrackingSocketService } from './tracking-socket.service';
import { parseApiMs } from '../tracking-format.util';
import type {
  TrackingSession, DashboardMetrics, TrackingWsEnvelope, TrackingSessionStatus,
  TrackingDataType, SystemAlert, TrackingProductScope,
} from '../tracking-demo.models';

/**
 * State container for the live dashboard. Component-provided, so its socket, ticker,
 * and safety-poll all die when the page is left.
 *
 * A signal `Map<sessionId, TrackingSession>` is the single source; the WS reducer does
 * idempotent, dedup'd, out-of-order-safe upserts, and `resync()` (REST snapshot) is
 * authoritative on init / reconnect / every 60s. One 1 Hz `now` signal drives every
 * ticking duration (one change-detection pass per second, no per-row timers).
 */
@Injectable()
export class TrackingStoreService implements OnDestroy {
  private readonly api = inject(TrackingApiService);
  private readonly socket = inject(TrackingSocketService);

  private readonly sessionsMap = signal<ReadonlyMap<string, TrackingSession>>(new Map());
  readonly metrics = signal<DashboardMetrics | null>(null);
  readonly now = signal<number>(this.epoch());
  readonly connectionState = this.socket.state;
  readonly loading = signal(false);
  /** Latest backend `system_alert` (e.g. Gemini credits exhausted). Sticky — shown as
   *  the blinking banner until the admin dismisses it; a re-fire refreshes it. */
  readonly systemAlert = signal<SystemAlert | null>(null);

  // filters (client-side for the live tab)
  readonly search = signal('');
  readonly statusFilter = signal<'ALL' | TrackingSessionStatus>('ALL');
  /**
   * The header's Dummy/Live toggle — server-side filter on every listing + the metrics
   * ('dummy' = internal @bridgintech.com test demos). Default 'live': admins see real
   * prospect data unless they flip to Dummy. WS events aren't classified, so a dummy
   * session can flash into the live list between resyncs — the next snapshot drops it.
   */
  readonly dataType = signal<TrackingDataType>('live');

  setDataType(dt: TrackingDataType): void {
    if (this.dataType() === dt) return;
    this.dataType.set(dt);
    void this.resync();
  }

  readonly sessions = computed(() =>
    [...this.sessionsMap().values()].sort(
      (a, b) => this.tsOf(b.startedAt ?? b.createdAt) - this.tsOf(a.startedAt ?? a.createdAt),
    ),
  );

  readonly filteredSessions = computed(() => {
    const q = this.search().trim().toLowerCase();
    const status = this.statusFilter();
    return this.sessions().filter((s) => {
      if (status !== 'ALL' && s.status !== status) return false;
      if (!q) return true;
      // "ios" / "android" / "app" find native-app launches by their declared platform.
      return [s.sessionId, s.companyName, s.hostName, s.security?.ip, s.security?.country,
        s.security?.browser, s.security?.os, s.security?.appPlatform,
        s.security?.appPlatform ? 'app' : '']
        .some((v) => (v || '').toString().toLowerCase().includes(q));
    });
  });

  private tickTimer: ReturnType<typeof setInterval> | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private readonly seenEvents = new Set<string>();  // eventId LRU for dedup
  private started = false;
  /**
   * The backend's resolution of this dashboard's product scope (echoed on every scoped
   * REST snapshot) — the WS reducer filters session events against it, because the
   * server fans out EVERY product's events on the shared admin socket.
   */
  private scope: TrackingProductScope | null = null;
  /** `ts` of the last dismissed system alert — see {@link applySystemAlert}. */
  private dismissedAlertTs: string | undefined;

  // ── lifecycle ──
  start(): void {
    if (this.started) return;
    this.started = true;
    this.tickTimer = setInterval(() => this.now.set(this.epoch()), 1000);
    // 60s safety poll keeps the page correct even if the socket is down.
    this.pollTimer = setInterval(() => this.resync(), 60_000);
    this.socket.connect({
      onEvent: (env) => this.applyEvent(env),
      onReconnected: () => this.resync(),
      onUnauthorized: () => { /* token invalidated by the socket; the connection chip shows Unauthorized */ },
    });
    this.resync();
  }

  ngOnDestroy(): void {
    if (this.tickTimer) clearInterval(this.tickTimer);
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.socket.close();
  }

  // ── REST resync (server is authoritative) ──
  async resync(): Promise<void> {
    this.loading.set(true);
    try {
      const [live, metrics] = await Promise.all([
        this.api.getLiveSessions(1, 200, this.dataType()),
        this.api.getMetrics(this.dataType()),
      ]);
      // Learn/refresh the resolved product scope (absent on an unscoped dashboard or a
      // pre-productization backend — the WS filter then stays permissive).
      this.scope = live.productScope ?? metrics.productScope ?? this.scope;
      const map = new Map<string, TrackingSession>();
      for (const s of live.items || []) map.set(s.sessionId, s);
      this.sessionsMap.set(map);
      this.metrics.set(metrics);
      // Retained alert from the snapshot — covers dashboards opened AFTER the firing
      // (the WS broadcast only reaches sockets that were open at publish time).
      if (metrics?.systemAlert) this.applySystemAlert(metrics.systemAlert);
    } catch {
      /* handled by api (401 → token invalidate); the socket retries otherwise */
    } finally {
      this.loading.set(false);
    }
  }

  // ── WS reducer (idempotent, dedup'd, out-of-order safe) ──
  applyEvent(env: TrackingWsEnvelope): void {
    if (env.eventId) {
      if (this.seenEvents.has(env.eventId)) return;
      this.seenEvents.add(env.eventId);
      if (this.seenEvents.size > 600) {
        // trim the oldest ~100 to bound memory
        const it = this.seenEvents.values();
        for (let i = 0; i < 100; i++) this.seenEvents.delete(it.next().value as string);
      }
    }
    const data = env.data || {};
    switch (env.type) {
      case 'session_started':
      case 'session_updated':
      case 'session_on_hold':
      case 'session_resumed':
        // The admin socket fans out EVERY product's events — a scoped dashboard only
        // applies its own product's sessions (REST snapshots are the authority anyway).
        if (this.matchesScope(data, env.sessionId)) this.upsertSession(data, env.ts);
        break;
      case 'session_ended':
      case 'admin_disconnected':
        if (this.matchesScope(data, env.sessionId)) {
          this.upsertSession({ ...data, status: data.status || (env.type === 'admin_disconnected' ? 'disconnected_by_admin' : 'ended') }, env.ts);
          // Drop from the live map after applying (history tab owns completed sessions).
          this.removeSession(data.sessionId || env.sessionId);
        }
        break;
      case 'participant_joined':
      case 'participant_left':
      case 'guest_requested':
        this.patchGuestCount(env.sessionId || data.sessionId, data.guestCount);
        break;
      case 'metrics':
        // Ignored: the sweeper's WS metrics are UNfiltered and would clobber the
        // Dummy/Live-scoped numbers; REST (resync + 60s poll) owns the metrics now.
        break;
      case 'ip_blacklisted':
      case 'ip_removed':
        // bump the metric optimistically; the blacklist tab refreshes itself
        this.bumpBlockedIps(env.type === 'ip_blacklisted' ? 1 : -1);
        break;
      case 'system_alert':
        // Operational alert (backend-throttled). Sticky until dismissed.
        this.applySystemAlert(data);
        break;
      default:
        break;
    }
  }

  /**
   * WS product-scope gate for session-level events.
   * - Unscoped dashboard (no productKey) → everything passes (legacy single-product mode).
   * - Scope not yet learned (first scoped resync hasn't landed) → drop; that concurrent
   *   resync is authoritative and repopulates the map, the 60s poll backstops.
   * - Event `productId` ABSENT (e.g. admin_disconnected sends only sid/reason) → fall back
   *   to session-map membership: it acts on our rows, no-ops on other products'.
   * - `productId` null = pre-rollout legacy row → owned by the default product (Credee).
   */
  private matchesScope(data: any, envSid?: string): boolean {
    if (!this.api.productKey()) return true;
    const s = this.scope;
    if (!s) return false;
    if (s.unfiltered) return true;
    const pid = data?.productId;
    if (pid === undefined) {
      const sid = data?.sessionId || envSid;
      return !!sid && this.sessionsMap().has(sid);
    }
    return pid === null ? s.includesLegacyNull : pid === s.productId;
  }

  /** Raise/refresh the banner. Skips the exact firing the admin already dismissed
   *  (`ts` identity) so the 60s metrics poll can't resurrect it — while a NEW firing
   *  (fresh ts) still brings the banner back. */
  private applySystemAlert(data: any): void {
    if (!data?.message) return;
    if (data.ts && data.ts === this.dismissedAlertTs) return;
    this.systemAlert.set({
      alert: data.alert || 'system',
      message: data.message,
      detail: data.detail,
      sessionId: data.sessionId,
      // A backend that predates the severity field only ever sent things worth a
      // red banner, so 'error' is the safe default — never silently downgrade.
      severity: data.severity === 'warn' ? 'warn' : 'error',
      ts: data.ts,
      receivedAt: Date.now(),
    });
  }

  dismissSystemAlert(): void {
    this.dismissedAlertTs = this.systemAlert()?.ts;
    this.systemAlert.set(null);
  }

  private upsertSession(data: any, ts?: string): void {
    const sid = data.sessionId;
    if (!sid) return;
    // A room "shadow" row (linkedSessionId set) is merged into the primary it points at —
    // the REST snapshot never lists one (list_active filters `linked_session_id IS NULL`),
    // so the socket must not either, or one demo shows TWICE: the real row plus a half-empty
    // twin (no agent, no start time, no security) that still offers a Join button and leaves
    // the admin guessing which link is the meeting. Removing heals a twin an older backend
    // already pushed in.
    if (data.linkedSessionId) { this.removeSession(sid); return; }
    const map = new Map(this.sessionsMap());
    const prev = map.get(sid);
    const eventTs = ts ? (parseApiMs(ts) ?? Date.now()) : Date.now();
    if (prev && prev._lastEventTs && eventTs < prev._lastEventTs) return; // stale, ignore
    // Terminal statuses shouldn't linger in the live map.
    if (['ended', 'expired', 'rejected'].includes(data.status)) {
      map.delete(sid);
      this.sessionsMap.set(map);
      return;
    }
    map.set(sid, { ...(prev || {}), ...data, _lastEventTs: eventTs } as TrackingSession);
    this.sessionsMap.set(map);
  }

  private patchGuestCount(sid: string | undefined, guestCount: number | undefined): void {
    if (!sid) return;
    const map = new Map(this.sessionsMap());
    const prev = map.get(sid);
    if (!prev) return;
    if (typeof guestCount === 'number') {
      map.set(sid, { ...prev, guestCount, participantCount: 1 + guestCount });
      this.sessionsMap.set(map);
    }
  }

  private removeSession(sid: string | undefined): void {
    if (!sid) return;
    const map = new Map(this.sessionsMap());
    if (map.delete(sid)) this.sessionsMap.set(map);
  }

  private bumpBlockedIps(delta: number): void {
    const m = this.metrics();
    if (m) this.metrics.set({ ...m, blockedIps: Math.max(0, m.blockedIps + delta) });
  }

  // ── helpers ──
  private epoch(): number { return new Date().getTime(); }
  private tsOf(iso?: string | null): number { return parseApiMs(iso) ?? 0; }
}
